import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Select, Input, message } from "antd";
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { PickersLayout } from '@mui/x-date-pickers';
import { styled } from '@mui/material/styles'
import { Link } from "react-router-dom";
import moment from "moment";
import { patchFollowUp } from "../../apis/usersApi";
import { fetchUsersByRoleIdForBranch } from "../../apis/rolesApi";
import Roles from "../../enums/Roles";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);
const userTz = dayjs.tz.guess();

// Styled layout shared for date pickers
export const StyledPickersLayout = styled(PickersLayout)({
  '.MuiDateCalendar-root': {
    color: 'black',
  },
  '.MuiButtonBase-root': {
      background: 'white!important',
      color: 'black!important'
  },
  '.MuiPickersYear-yearButton':{
      background: 'white!important',
      color: 'black!important'
  },
  '.Mui-disabled':{
      color: 'rgba(0, 0, 0, 0.38)!important'
  },
  '.Mui-selected':{
      background: 'linear-gradient(90deg, rgba(67,0,36,1) 0%, rgba(67,5,68,1) 72%, rgba(87,6,88,1) 100%)!important',
      color:'white!important'
  },
})

// factory to create columns for both upcoming and past courtesies
export const useCourtesiesColumns = () => {
  const [courtesiesFollowers, setCourtesiesFollowers] = useState([]);

  useEffect(() => {
    const load = async () => {
      try{
        const branch_id = localStorage.getItem('branch_id');
        const response = await fetchUsersByRoleIdForBranch(Roles.COURTESY_FOLLOWER, branch_id);
        setCourtesiesFollowers(response);
      } catch (error) {
        console.error('UserList - loadCourtesyFollowers, Error fetching courtesy followers:', error);
        message.error("Error al cargar los seguidores. Contacta al soporte");
        setCourtesiesFollowers([]);
      }
    };
    load();
  }, []);

  const handleEdit = useCallback(async (user_id, field, value) => {
    try {
      const params = { [field]: value };
      await patchFollowUp(user_id, params);
      message.success("Actualizado!");
    } catch {
      message.error("Actualización fallida");
    }
  }, []);

  const rowClassName = useCallback((record) =>
    record.plan_name ?
      "table-success" :
    (record.level_of_interest >= 2 ||
      (record.next_contact_date && dayjs.utc(record.next_contact_date).isSameOrBefore(dayjs.utc())))
      ? "table-danger"
      : "", []);

  const columns = useMemo(() => [
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <Link to={`/users/${record.user_id}`}>
          {record.name}{record.surname_1 ? ` ${record.surname_1}` : ""}
        </Link>
      ),
    },
    { title: "Teléfono", dataIndex: "phone", key: "phone" },
    { title: "Clase", dataIndex: "event_name", key: "event_name" },
    {
      title: "Fecha de cortesía",
      dataIndex: "start_date",
      key: "start_date",
      render: text => dayjs.utc(text).tz(userTz).format("DD/MM/YYYY HH:mm"),
    },
    {
      title: "Asistió",
      dataIndex: "attended",
      key: "attended",
      render: val => (val ? "Sí" : "No"),
    },
    {
      title: "Se Inscribió",
      dataIndex: "plan_name",
      key: "se_inscribio",
      render: (plan_name) => (
        <span>{plan_name ? "Sí" : "No"}</span>
      ),
    },
    {
      title: "Interés",
      dataIndex: "level_of_interest",
      key: "level_of_interest",
      render: (value, record) => {
        const levelOptions = [
          { value: 3, label: "Muy alto" },
          { value: 2, label: "Alto" },
          { value: 1, label: "Medio" },
          { value: 0, label: "Bajo" },
        ];

        return (
          <Select
            value={value !== null && value !== undefined ? value : undefined}
            style={{ minWidth: 120 }}
            placeholder="Seleccione el interés"
            onChange={(val) => handleEdit(record.user_id, "level_of_interest", val)}
            options={levelOptions}
            disabled={!!record.plan_name}
          />
        );
      },
    },
    {
      title: "Seguidor",
      dataIndex: "follower_id",
      key: "follower_id",
      render: (value, record) => (
        <Select
          value={value ?? undefined}
          style={{ minWidth: 120 }}
          placeholder="Seleccione un seguidor"
          allowClear
          onChange={val => handleEdit(record.user_id, "follower_id", (val === undefined || val === "") ? null : val)}
          options={courtesiesFollowers.map(f => ({ value: f.id, label: f.name }))}
          disabled={!!record.plan_name}
        />
      ),
    },
    {
      title: "Fecha de Contacto",
      dataIndex: "contact_date",
      key: "contact_date",
      render: (value, record) => (
        <MobileDatePicker
          style={{ minWidth: 150 }}
          value={value ? moment.utc(value) : null} /*MobileDatePicker expects a moment object*/
          onChange={date => handleEdit(record.user_id, "contact_date", date ? date.format("YYYY-MM-DD") : null)}
          maxDate={moment()} /*MobileDatePicker expects a moment object*/
          format="DD/MM/YYYY"
          slots={{ layout: StyledPickersLayout }}
          disabled={!!record.plan_name}
        />
      ),
    },
    {
      title: "Próximo Contacto",
      dataIndex: "next_contact_date",
      key: "next_contact_date",
      render: (value, record) => (
        <MobileDatePicker
          style={{ minWidth: 120 }}
          value={value ? moment(value).utc() : null} /*MobileDatePicker expects a moment object*/
          onChange={date => handleEdit(record.user_id, "next_contact_date", date ? date.format("YYYY-MM-DD") : null)}
          minDate={moment()} /*MobileDatePicker expects a moment object*/
          slots={{ layout: StyledPickersLayout }}
          disabled={!!record.plan_name}
        />
      ),
    },
    {
      title: "Respuesta",
      dataIndex: "response",
      key: "response",
      render: (value, record) => (
        <Input.TextArea
          defaultValue={value}
          rows={3}
          style={{ minWidth: 300 }}
          onBlur={e => handleEdit(record.user_id, "response", e.target.value)}
          disabled={!!record.plan_name}
        />
      ),
    },
    {
      title: "Notas",
      dataIndex: "notes",
      key: "notes",
      render: (value, record) => (
        <Input.TextArea
          defaultValue={value}
          rows={3}
          style={{ minWidth: 300 }}
          onBlur={e => handleEdit(record.user_id, "notes", e.target.value)}
          disabled={!!record.plan_name}
        />
      ),
    },
  ], [courtesiesFollowers, handleEdit]);

  return { columns, rowClassName };
};

export default useCourtesiesColumns;
